import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthArea } from "../components/AuthArea.js";
import { useGameStore } from "../store/useGameStore.js";
import { useI18n } from "../i18n/index.js";
import type { TranslationKey } from "../i18n/index.js";

// ============================================================
// Datenstrukturen
// ============================================================

/** Spielmodus-Gruppe mit zwei Buttons (Ziehe 1 + Klondike) */
interface ModeSection {
  id: string;
  titleKey: TranslationKey;
  subKey: TranslationKey;
  image?: string;
  emoji?: string;
  draw1Route: string;
  klondikeRoute: string;
}

/** Einzelner Modus (disabled, kein Sub-Button) */
interface DisabledMode {
  id: string;
  titleKey: TranslationKey;
  subKey: TranslationKey;
  image?: string;
  emoji?: string;
}

const SECTIONS: ModeSection[] = [
  {
    id: "daily",
    titleKey: "start.setOfDay",
    subKey: "start.setOfDay.sub",
    image: "set-of-the-day.png",
    draw1Route: "/game?mode=daily",
    klondikeRoute: "/game?mode=klondike",
  },
  {
    id: "journey",
    titleKey: "start.journey",
    subKey: "start.journey.sub",
    image: "journey.png",
    draw1Route: "/game?mode=journey",
    klondikeRoute: "/game?mode=journey-klondike",
  },
];

const DISABLED_MODES: DisabledMode[] = [
  { id: "weekly", titleKey: "start.weekly", subKey: "start.weekly.sub", image: "weekly.png" },
  { id: "tower",  titleKey: "start.tower",  subKey: "start.tower.sub",  emoji: "🗼" },
];

// ============================================================
// StartScreen
// ============================================================

export function StartScreen() {
  const navigate = useNavigate();
  const { t } = useI18n();

  // Journey-Init-Flag zurücksetzen damit beim nächsten Eintritt aus DB geladen wird
  useEffect(() => {
    useGameStore.setState({ _journeyInitDone: false });
  }, []);

  return (
    <div className="h-full overflow-y-auto relative">
      {/* Auth oben rechts */}
      <div className="absolute top-3 right-3 z-10">
        <AuthArea />
      </div>

      <div className="min-h-full flex flex-col items-center px-4 py-10 gap-8">
        <h1 className="text-white text-2xl font-bold tracking-wide mb-2">
          {t("start.title")}
        </h1>

        {/* Aktive Modi mit Sub-Buttons */}
        {SECTIONS.map((section) => (
          <div key={section.id} className="w-full max-w-sm">
            {/* Bild / Emoji */}
            <div className="flex items-center justify-center" style={{ height: 140 }}>
              {section.image ? (
                <img
                  src={`/images/${section.image}`}
                  alt={t(section.titleKey)}
                  className="max-h-full object-contain"
                />
              ) : (
                <span className="text-white/30 text-6xl font-bold">{section.emoji}</span>
              )}
            </div>

            {/* Titel + Untertitel */}
            <div className="mt-2 text-center">
              <span className="text-white font-semibold text-base">
                {t(section.titleKey)}
              </span>
              <p className="text-white/40 text-xs mt-0.5">{t(section.subKey)}</p>
            </div>

            {/* Zwei Buttons: Ziehe 1 | Klondike */}
            <div className="flex gap-3 mt-3 justify-center">
              <button
                onClick={() => navigate(section.draw1Route)}
                className="flex-1 max-w-[10rem] px-4 py-2.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition-colors hover:scale-[1.02] active:scale-[0.98]"
              >
                {t("start.draw1")}
              </button>
              <button
                onClick={() => navigate(section.klondikeRoute)}
                className="flex-1 max-w-[10rem] px-4 py-2.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition-colors hover:scale-[1.02] active:scale-[0.98]"
              >
                {t("start.klondike")}
              </button>
            </div>
          </div>
        ))}

        {/* Disabled Modi (ohne Sub-Buttons) */}
        {DISABLED_MODES.map((mode) => (
          <div key={mode.id} className="w-full max-w-sm opacity-40">
            <div className="flex items-center justify-center" style={{ height: 140 }}>
              {mode.image ? (
                <img
                  src={`/images/${mode.image}`}
                  alt={t(mode.titleKey)}
                  className="max-h-full object-contain"
                />
              ) : (
                <span className="text-white/30 text-6xl font-bold">{mode.emoji}</span>
              )}
            </div>
            <div className="mt-2 text-center">
              <span className="text-white font-semibold text-base">
                {t(mode.titleKey)}
              </span>
              <span className="ml-2 text-[10px] uppercase tracking-wider bg-white/20 text-white/50 px-2 py-0.5 rounded-full">
                {t("start.soon")}
              </span>
              <p className="text-white/40 text-xs mt-0.5">{t(mode.subKey)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
